import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { idFromSlug, cookbookPath, recipePath } from "@/lib/slug";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { FacebookLogo, XLogo, WhatsappLogo, TelegramLogo, InstagramLogo, TiktokLogo, Envelope, RedditLogo } from "@phosphor-icons/react";
import { Plus, Printer, QrCode, Share, BookOpenText, PencilSimple, DownloadSimple, Copy, GearSix, UserPlus, X, Trash } from "@phosphor-icons/react";
import BookCover from "@/components/BookCover";
import PageMeta from "@/components/PageMeta";
import PrintImageGuardModal from "@/components/PrintImageGuardModal";
import { useBulkSelect, BulkRecipeGrid, BulkMoveBar, withoutIds, withTags } from "@/components/BulkMove";
import CookbookJsonLd from "@/components/CookbookJsonLd";
import ShareCookbookDialog from "@/components/ShareCookbookDialog";
import { EmailShareDialog } from "@/components/EmailShareDialog";
import CookbookEditorsPanel from "@/components/CookbookEditorsPanel";
import PrintPrepareModal from "@/components/PrintPrepareModal";
import { BulkPrivacyButtons } from "@/components/BulkPrivacyButtons";
import { SsrFallback, hasSsr } from "@/lib/ssrSnapshot";
import { groupTags, canonicalTagSignature } from "@/lib/tags";

export default function CookbookView() {
  const { id: slugId } = useParams();
  const id = idFromSlug(slugId);
  const { user } = useAuth();
  const { flags } = useSiteFlags();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cb, setCb] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printPrepareOpen, setPrintPrepareOpen] = useState(false);
  const [activeTag, setActiveTag] = useState(null); // canonical signature or null
  const [sortBy, setSortBy] = useState("newest"); // newest | title
  const [activeLetter, setActiveLetter] = useState(null); // A..Z or "#" for non-alpha
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [titleSearch, setTitleSearch] = useState("");
  // Tag chip UX — hide the long tail by default so a cookbook with 60+
  // tags doesn't dump the entire list into the filter row.
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  // Backfill progress (admin only; only relevant for owned/admin cookbooks).
  const [backfillJob, setBackfillJob] = useState(null);
  const [printGuardOpen, setPrintGuardOpen] = useState(false);
  // Rename prompt for freshly imported cookbooks — triggered by
  // `?just_imported=1` query param set by the import auto-commit hand-off.
  const [renamePrompt, setRenamePrompt] = useState(null);   // { title } | null
  const [savingRename, setSavingRename] = useState(false);
  const sel = useBulkSelect();

  useEffect(() => {
    Promise.all([
      api.get(`/cookbooks/${id}`),
      api.get(`/recipes/by-cookbook/${id}`),
    ]).then(([a, b]) => {
      setCb(a.data); setRecipes(b.data);
      // If we arrived via ?just_imported=1 (import auto-commit hand-off),
      // open a friendly rename prompt seeded with the current title so
      // imports don't stay at "Imported from Paprika · Sep 2026" forever.
      if (params.get("just_imported") === "1" && a.data?.owner_id === user?.user_id) {
        setRenamePrompt({ title: a.data.title || "" });
        // Drop the query param so a page refresh doesn't re-open the modal.
        params.delete("just_imported");
        setParams(params, { replace: true });
      }
    }).catch(() => toast.error("Cookbook not accessible"));
  }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const saveRename = async () => {
    const newTitle = (renamePrompt?.title || "").trim();
    if (!newTitle || newTitle === cb?.title) { setRenamePrompt(null); return; }
    setSavingRename(true);
    try {
      await api.patch(`/cookbooks/${id}`, { title: newTitle });
      setCb((c) => ({ ...c, title: newTitle }));
      toast.success("Cookbook renamed");
      setRenamePrompt(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally {
      setSavingRename(false);
    }
  };

  // Auto-refresh the recipe list when the tab regains focus or the
  // browser tab becomes visible again. This makes the common iOS
  // Shortcut flow feel like an app: cook shares from Instagram → returns
  // to Safari → the recipe is already sitting in the list. Belt-and-
  // braces: also polls silently every 25s while the tab stays visible,
  // so the list refreshes even if the user never switches tabs.
  // On each refresh, if the recipe count grew we surface a subtle toast
  // so the cook knows the new arrival is theirs (not just React re-
  // rendering old data).
  useEffect(() => {
    if (!id) return undefined;
    let lastCount = null;
    let pollTimer = null;
    let cancelled = false;

    const refresh = async () => {
      try {
        // `?fresh=…` bypasses the service worker's stale-while-revalidate
        // cache so we see the ACTUAL current recipe list after the cook
        // returns from Instagram / Shortcuts. Without it we'd get the
        // pre-share cached list.
        const r = await api.get(`/recipes/by-cookbook/${id}?fresh=${Date.now()}`);
        if (cancelled) return;
        const nextCount = Array.isArray(r.data) ? r.data.length : 0;
        setRecipes(r.data);
        if (lastCount !== null && nextCount > lastCount) {
          const added = nextCount - lastCount;
          toast.success(
            added === 1 ? "New recipe just landed 📥" : `${added} new recipes just landed 📥`,
            { description: "Fresh from your iOS share — scroll down to see it.", duration: 5000 },
          );
        }
        lastCount = nextCount;
      } catch {
        /* silent — probably a transient network blip; next tick will retry */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        if (document.visibilityState === "visible") refresh();
      }, 25000);
    };
    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    // Seed lastCount from whatever's already rendered so the first
    // background refresh doesn't fire a bogus "new recipe" toast.
    lastCount = recipes.length;

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    startPolling();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
      stopPolling();
    };
  }, [id]);

  // Owner/Admin: AI-backfill the 30-word summary on every recipe in this
  // cookbook that hasn't got one yet. Kicks off the background ai_job and
  // polls status every 3s until done. We reload the recipe list once the
  // job finishes so the tiles pick up the fresh summaries.
  const runBackfillSummary = async () => {
    try {
      const { data } = await api.post("/admin/recipes/backfill-summary", { cookbook_id: id });
      if (data.status === "noop") {
        toast(data.message || "Every recipe already has a summary.");
        return;
      }
      setBackfillJob({ ...data, processed: 0 });
      toast(`Drafting summaries for ${data.requested} recipes…`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: status } = await api.get(`/admin/recipes/backfill-summary/status/${jobId}`);
          setBackfillJob(status);
          if (["done", "error", "cancelled"].includes(status.status)) {
            if (status.status === "done") {
              toast.success(`Summaries drafted: ${status.inserted || 0} written, ${status.skipped || 0} skipped.`);
              // Reload so tiles refresh — recipes are shown in `recipes` state.
              const r = await api.get(`/recipes/by-cookbook/${id}`);
              setRecipes(r.data);
            } else if (status.status === "error") {
              toast.error(status.error || "Backfill failed.");
            }
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Poll failed");
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start backfill");
    }
  };

  // Soft-delete: move this cookbook into the 30-day Trash. The owner
  // can tap Restore from the Dashboard any time before the grace period
  // ends. No scary type-the-title confirmation because the action is
  // reversible — a single tap on the modal's confirm button is enough.
  const handleDeleteCookbook = async () => {
    if (!cb) return;
    setDeleting(true);
    try {
      await api.delete(`/cookbooks/${cb.cookbook_id}`);
      toast.success(`"${cb.title}" moved to Trash — 30 days to restore.`);
      setDeleteOpen(false);
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed. Please try again.");
      setDeleting(false);
    }
  };

  if (!cb) return hasSsr(window.location.pathname) ? <SsrFallback pathname={window.location.pathname} /> : <div className="p-10 text-center">Loading…</div>;

  const isOwner = user?.user_id === cb.owner_id;
  const isCoEditor = !!(user && Array.isArray(cb.co_editor_ids) && cb.co_editor_ids.includes(user.user_id));
  const mine = (r) => !!user && r.owner_id === user.user_id;
  const isAdmin = user?.role === "Admin" || user?.role === "SuperAdmin";
  const canEdit = isOwner || isCoEditor || isAdmin;

  // Sort recipes. Only two sorts now: newest addition or title A-Z. "Cook's
  // name" was removed on user feedback — nobody uses it and the field is
  // often blank.
  const sortedRecipes = recipes.slice().sort((a, b) => {
    if (sortBy === "title") return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  // First-letter buckets — used both to show only letters that actually have
  // recipes AND to power the A-Z chip filter. Non-alphabetic titles land
  // under "#".
  const _firstLetter = (t) => {
    const ch = (t || "").trim().charAt(0).toUpperCase();
    return /[A-Z]/.test(ch) ? ch : "#";
  };
  const letterCounts = {};
  recipes.forEach((r) => { const l = _firstLetter(r.title); letterCounts[l] = (letterCounts[l] || 0) + 1; });
  const availableLetters = Object.keys(letterCounts).sort();

  // Derive tag list + counts, then collapse case/plural/hyphen variants
  // so the chip row stays tidy. Each canonical bucket keeps its list of
  // original variants so filtering still matches every recipe.
  const rawCounts = {};
  sortedRecipes.forEach((r) => (r.tags || []).forEach((t) => { rawCounts[t] = (rawCounts[t] || 0) + 1; }));
  const tagGroups = groupTags(Object.entries(rawCounts).map(([tag, count]) => ({ tag, count })));
  // Optional client-side filter box (visible once expanded).
  const tagFilterTrim = tagFilter.trim().toLowerCase();
  const filteredTagGroups = tagFilterTrim
    ? tagGroups.filter((g) =>
        g.label.toLowerCase().includes(tagFilterTrim)
        || g.variants.some((v) => v.toLowerCase().includes(tagFilterTrim)),
      )
    : tagGroups;
  const TAG_COLLAPSE_LIMIT = 12;
  const visibleTagGroups = tagsExpanded ? filteredTagGroups : filteredTagGroups.slice(0, TAG_COLLAPSE_LIMIT);
  const activeGroupVariants = activeTag
    ? (tagGroups.find((g) => g.signature === activeTag)?.variants || [])
    : [];

  // Apply title search + letter + tag filters
  const titleSearchTrim = titleSearch.trim().toLowerCase();
  const filtered = sortedRecipes.filter((r) => {
    if (activeTag) {
      const recipeSigs = new Set((r.tags || []).map((t) => canonicalTagSignature(t)));
      if (!recipeSigs.has(activeTag)) return false;
    }
    if (activeLetter && _firstLetter(r.title) !== activeLetter) return false;
    if (titleSearchTrim && !((r.title || "").toLowerCase().includes(titleSearchTrim))) return false;
    return true;
  });
  const recipesNeedingImages = recipes.filter((r) => Boolean(r.video_url) && (!r.images || r.images.length === 0));

  const reloadRecipes = async () => {
    try {
      const r = await api.get(`/recipes/by-cookbook/${id}`);
      setRecipes(r.data);
    } catch { /* no-op */ }
  };

  const downloadA4Pdf = async () => {
    setPrinting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/print/cookbook/${id}/a4`;
      const token = localStorage.getItem("rf_token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) {
        // Server returns a helpful "detail" for known failures (empty
        // cookbook, corrupt image, etc). Surface it verbatim so the user
        // knows what to try next instead of just seeing "download failed".
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `PDF request failed (HTTP ${res.status}). Please try again or contact contactus@recipefiler.com.`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${(cb.title || "cookbook").replace(/\s+/g, "_")}_A4_ring_binder.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("PDF downloaded — print it on A4 and drop into your ring binder.");
    } catch (e) {
      // Duration extended so users can actually read the fix suggestion.
      toast.error(e?.message || "PDF download failed", { duration: 10_000 });
    } finally { setPrinting(false); }
  };

  const downloadBookPdf = async () => {
    if (recipesNeedingImages.length > 0) {
      setPrintGuardOpen(true);
      return;
    }
    setPrinting(true);
    try {
      // Download the 6×9" book-format PDF (any print service)
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/print/cookbook/${id}/lulu`;
      const token = localStorage.getItem("rf_token");
      const res = await fetch(pdfUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${(cb.title || "cookbook").replace(/\s+/g, "_")}_6x9_book.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Book-format PDF downloaded — send it to any print service or print shop.", { duration: 6000 });
    } catch (e) {
      toast.error(e?.message || "Book PDF generation failed");
    } finally { setPrinting(false); }
  };

  const cookbookUrl = `${window.location.origin}${cookbookPath(cb)}`;

  return (
    <div className="bg-paper min-h-screen">
      {/* SEO — pulls admin-editable overrides from the cookbook doc when set,
          falls back to the cookbook title + description. Falls back further
          to the site default when both are absent. Only public cookbooks
          get their metadata surfaced to search engines — private ones stay
          noindex regardless. */}
      <PageMeta
        title={cb.seo_title || cb.title || "Cookbook"}
        description={cb.seo_description || cb.description || undefined}
        keywords={cb.seo_keywords || undefined}
        ogImage={cb.cover_image_url || undefined}
        noindex={cb.privacy !== "public"}
      />
      {/* JSON-LD CollectionPage + ItemList — only injected for public cookbooks
          so private ones never leak into Google's index. */}
      <CookbookJsonLd cookbook={cb} recipes={recipes} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {(() => {
          // If the user arrived from the Directory (either via referrer OR by
          // clicking a link with the `?from=directory` query param — which
          // survives share links & new tabs), send them back there. Otherwise
          // default to their own Dashboard.
          const params = new URLSearchParams(window.location.search);
          const fromParam = params.get("from");
          const cameFromDirectory = fromParam === "directory"
            || (typeof document !== "undefined" && document.referrer && /\/directory(\?|$|\/)/.test(document.referrer));
          const backTo = cameFromDirectory ? "/directory" : "/dashboard";
          const backLabel = cameFromDirectory ? "Directory" : "Dashboard";
          return (
            <Link
              to={backTo}
              data-testid="cookbook-back-link"
              className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block"
            >← {backLabel}</Link>
          );
        })()}

        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5">
            <BookCover cookbook={cb} size="lg" data-testid="cb-front-cover" />
          </div>

          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="chip">{cb.privacy.replace(/_/g, " ")}</div>
              {cb.is_system && cb.system_kind === "inbox" && <div className="chip !bg-rf-amber !text-rf-ink font-bold" data-testid="cb-inbox-chip">★ Inbox</div>}
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink leading-[0.95] mb-4">{cb.title}</h1>
            <p className="text-rf-ink-soft text-lg leading-relaxed mb-8">{cb.description}</p>

            {isOwner && cb.is_system && cb.system_kind === "inbox" && (
              <div className="mb-6 p-4 rounded-2xl bg-rf-amber/15 border border-rf-amber/40 text-sm text-rf-ink" data-testid="cb-inbox-hint">
                <b>This is your Inbox.</b> Recipes you share or import from your phone land here. Use the <b>Move to →</b> button on each recipe to file it into one of your cookbooks.
              </div>
            )}

            {isOwner && (
              <div className="mb-6 card-recipe !bg-rf-cream-alt">
                <p className="overline mb-2">View-only code</p>
                <p className="font-mono text-2xl font-black text-rf-orange tracking-widest">{cb.invite_code}</p>
                <p className="text-xs text-rf-ink-soft mt-1">
                  Anyone with this 10-digit code can <b>view</b> the cookbook — but they can&apos;t edit it.
                  To grant co-editor access, use <b>Invite a friend</b> to send a unique, single-use email invite.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              {canEdit && <Link to={`/scan?cookbook_id=${id}&return_to=${encodeURIComponent(cookbookPath(cb))}`} data-testid="cb-add-recipe" className="btn-amber"><Plus size={18} /> Add recipe</Link>}
              {canEdit && <Link to={`${cookbookPath(cb)}/edit`} data-testid="cb-edit" className="btn-outline-ink"><GearSix size={18} /> Customise</Link>}
              {isOwner && (
                <button
                  data-testid="cb-invite-friend"
                  onClick={() => setShowInvite(true)}
                  className="btn-outline-ink"
                ><UserPlus size={18} /> Invite a friend</button>
              )}
              <button data-testid="cb-qr" onClick={() => setShowQR(true)} className="btn-outline-ink"><QrCode size={18} /> QR / Share</button>
              {/* Unified "Prepare for print" modal — step 1 lets the owner
                  edit a Foreword and preview every recipe with its photo,
                  step 2 offers the A4 PDF or the book-format PDF. Replaces the
                  older direct download / order buttons which short-cut the
                  preview + foreword flow. */}
              {isOwner && (
                <button
                  data-testid="cb-print-prepare"
                  onClick={() => setPrintPrepareOpen(true)}
                  disabled={recipes.length === 0}
                  className="btn-amber"
                >
                  <Printer size={18} /> Prepare for print
                </button>
              )}
              {/* Delete button — owner only, and never on the Inbox
                  system cookbook (the backend 400s that path anyway).
                  Routed through a two-step confirmation modal below
                  because the DELETE is destructive and cascades to
                  every recipe inside. */}
              {isOwner && !cb.is_system && (
                <button
                  data-testid="cb-delete"
                  onClick={() => { setDeleteOpen(true); }}
                  className="btn-outline-ink !text-amber-800 !border-amber-300 hover:!bg-amber-50"
                  title="Move this cookbook to Trash — restorable for 30 days"
                >
                  <Trash size={18} /> Move to Trash
                </button>
              )}
              {isCoEditor && !isOwner && (
                <span data-testid="cb-coeditor-badge" className="chip !bg-blue-100 !text-blue-800 self-center">Co-editor — owner controls publishing</span>
              )}
              {/* Admin-only: fire an AI backfill of the 30-word "About this
                  recipe" summary for every recipe in this cookbook missing
                  one. Uses the /admin/recipes/backfill-summary endpoint's
                  ai_jobs background flow so a 200-recipe book won't trip
                  the proxy timeout. */}
              {isAdmin && (
                <button
                  data-testid="cb-backfill-summary"
                  onClick={runBackfillSummary}
                  disabled={backfillJob?.status === "running"}
                  className="btn-outline-ink"
                  title="AI-draft a 30-word summary on every recipe missing one"
                >
                  {backfillJob?.status === "running"
                    ? `Backfilling summaries · ${backfillJob.processed || 0}/${backfillJob.total || 0}`
                    : "Backfill AI summaries"}
                </button>
              )}
              {!!user && recipes.some(mine) && (
                <button type="button" data-testid="bulk-select-start-btn" className="btn-outline-ink"
                        onClick={() => { sel.selecting ? sel.stop() : sel.start(); document.querySelector('[data-testid="cb-recipe-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                  {sel.selecting ? "Done selecting" : "Bulk Recipe Tag/Move/Delete"}
                </button>
              )}
              {isOwner && recipes.length > 0 && <BulkPrivacyButtons cookbookId={cb.cookbook_id} onDone={reloadRecipes} />}
            </div>
          </div>
        </div>

        {isOwner && <CookbookEditorsPanel cookbookId={cb.cookbook_id} />}

        {/* Sort + title search + A-Z + tag filter */}
        <div className="mb-8">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <p className="overline mb-1">Recipes</p>
              <h2 className="font-display text-3xl font-black text-rf-ink">{recipes.length} recipe{recipes.length === 1 ? "" : "s"} in this book{activeTag || activeLetter || titleSearchTrim ? ` · ${filtered.length} shown` : ""}</h2>
            </div>
            {recipes.length > 1 && (
              <div className="flex items-center gap-2" data-testid="cb-sort-controls">
                <label htmlFor="cb-sort" className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft">Sort by</label>
                <select
                  id="cb-sort"
                  data-testid="cb-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input-rf !py-2 !text-sm !w-auto min-w-[160px]"
                >
                  <option value="newest">Newest addition</option>
                  <option value="title">Alphabetical (title)</option>
                </select>
              </div>
            )}
          </div>

          {/* Recipe title search — filters by "contains" so partial names find
              a recipe. Combines with letter/tag filters below (all AND'd). */}
          {recipes.length > 2 && (
            <div className="mb-4">
              <label htmlFor="cb-title-search" className="sr-only">Search recipes by title</label>
              <div className="relative max-w-xl">
                <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-rf-ink-soft" aria-hidden="true" />
                <input
                  id="cb-title-search"
                  type="text"
                  data-testid="cb-title-search"
                  value={titleSearch}
                  onChange={(e) => setTitleSearch(e.target.value)}
                  placeholder="Search recipes by title…"
                  className="input-rf !pl-11"
                />
                {titleSearch && (
                  <button
                    type="button"
                    data-testid="cb-title-search-clear"
                    onClick={() => setTitleSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-rf-ink-soft hover:text-rf-ink w-6 h-6 rounded-full hover:bg-rf-cream-alt flex items-center justify-center"
                    aria-label="Clear search"
                  ><X size={14} /></button>
                )}
              </div>
            </div>
          )}

          {/* A-Z chip filter — only render letters that actually have recipes
              so an empty book doesn't dump 26 disabled chips on-screen. */}
          {availableLetters.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="cb-letter-filter">
              <span className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft mr-1">A-Z</span>
              <button
                type="button"
                data-testid="cb-letter-all"
                onClick={() => setActiveLetter(null)}
                className={`min-w-[28px] h-7 px-2 rounded-full text-xs font-bold transition-all ${!activeLetter ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
              >All</button>
              {availableLetters.map((L) => (
                <button
                  type="button"
                  key={L}
                  data-testid={`cb-letter-${L}`}
                  onClick={() => setActiveLetter(L === activeLetter ? null : L)}
                  title={`${letterCounts[L]} recipe${letterCounts[L] === 1 ? "" : "s"} starting with ${L}`}
                  className={`min-w-[28px] h-7 px-2 rounded-full text-xs font-bold transition-all ${activeLetter === L ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
                >{L}</button>
              ))}
            </div>
          )}

          {tagGroups.length > 0 && (
            <div className="mb-4" data-testid="cb-tag-filters">
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  data-testid="cb-filter-all"
                  onClick={() => setActiveTag(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${!activeTag ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
                >All tags · {recipes.length}</button>
                {visibleTagGroups.map((g) => (
                  <button
                    key={g.signature}
                    data-testid={`cb-filter-${g.signature}`}
                    onClick={() => setActiveTag(g.signature === activeTag ? null : g.signature)}
                    title={g.variants.length > 1 ? `Includes: ${g.variants.join(", ")}` : g.label}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${activeTag === g.signature ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
                  >
                    {g.label}
                    {g.count ? ` · ${g.count}` : ""}
                    {g.variants.length > 1 && (
                      <span className="ml-1 text-[9px] opacity-60" title={`Collapses ${g.variants.length} variant spellings`}>×{g.variants.length}</span>
                    )}
                  </button>
                ))}
                {filteredTagGroups.length > TAG_COLLAPSE_LIMIT && (
                  <button
                    onClick={() => setTagsExpanded((v) => !v)}
                    data-testid="cb-filter-toggle-more"
                    className="px-3 py-1.5 rounded-full text-xs font-bold transition-all bg-white border border-rf-ink/15 text-rf-ink hover:bg-rf-cream"
                  >
                    {tagsExpanded
                      ? "Show fewer"
                      : `Show all ${filteredTagGroups.length} tags`}
                  </button>
                )}
              </div>
              {tagsExpanded && tagGroups.length > TAG_COLLAPSE_LIMIT && (
                <input
                  type="text"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  placeholder="Search tags…"
                  data-testid="cb-tag-search"
                  className="mt-3 w-full max-w-xs rounded-full border border-rf-ink/15 px-3 py-1.5 text-xs focus:outline-none focus:border-rf-amber"
                />
              )}
              {activeTag && activeGroupVariants.length > 1 && (
                <p className="mt-2 text-[11px] text-rf-ink-soft" data-testid="cb-filter-variants-note">
                  Also matches: {activeGroupVariants.filter((v) => canonicalTagSignature(v) === activeTag).join(" · ")}
                </p>
              )}
            </div>
          )}
        </div>

        {recipes.length === 0 ? (
          <div className="card-recipe text-center py-16">
            <BookOpenText size={48} className="mx-auto text-rf-orange mb-4" />
            <p className="text-rf-ink-soft mb-4">No recipes here yet.</p>
            {isOwner && <Link to={`/scan?cookbook_id=${id}&return_to=${encodeURIComponent(cookbookPath(cb))}`} className="btn-amber">Scan your first recipe</Link>}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-recipe text-center py-12">
            <p className="text-rf-ink-soft mb-3">No recipes match this tag yet.</p>
            <button onClick={() => setActiveTag(null)} className="btn-outline-ink">Show all recipes</button>
          </div>
        ) : (
          <>
            <BulkRecipeGrid recipes={filtered} sel={sel} canSelect={mine} variant="cookbook" testid="cb-recipe-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" />
            <BulkMoveBar sel={sel} selectable={filtered.filter(mine)} excludeCookbookId={cb.cookbook_id}
                         onMoved={(res) => setRecipes(withoutIds(res))} onTrashed={(res) => setRecipes(withoutIds(res))}
                         onTagged={(res) => setRecipes(withTags(res))} />
          </>
        )}
      </div>

      {showQR && (
        <div className="fixed inset-0 z-50 bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-2xl text-rf-ink mb-4">{cb.title}</h3>
            <div className="flex justify-center my-4">
              <div id="cb-qr-canvas">
                <QRCodeCanvas value={cookbookUrl} size={220} fgColor="#EF8200" bgColor="#FDFBF7" includeMargin />
              </div>
            </div>
            <p className="text-xs text-rf-ink-soft break-all mb-5">{cookbookUrl}</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                data-testid="cb-qr-download"
                onClick={() => {
                  const canvas = document.querySelector("#cb-qr-canvas canvas");
                  if (!canvas) return;
                  const link = document.createElement("a");
                  link.download = `${cb.title.replace(/\s+/g, "-").toLowerCase()}-qr.png`;
                  link.href = canvas.toDataURL("image/png");
                  link.click();
                  toast.success("QR downloaded");
                }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <DownloadSimple size={14} /> PNG
              </button>
              <button
                data-testid="cb-qr-copy"
                onClick={async () => { await navigator.clipboard.writeText(cookbookUrl); toast.success("Link copied"); }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <Copy size={14} /> Copy
              </button>
              <button
                data-testid="cb-qr-share"
                onClick={async () => {
                  if (navigator.share) {
                    try { await navigator.share({ title: cb.title, text: `Check out my cookbook: ${cb.title}`, url: cookbookUrl }); }
                    catch { /* user cancelled native share */ }
                  } else {
                    await navigator.clipboard.writeText(cookbookUrl);
                    toast.success("Link copied — paste to share");
                  }
                }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <Share size={14} /> Share
              </button>
            </div>
            {/* Social share row — mirrors the recipe Share dialog. Instagram +
                TikTok don't accept web-share intents so we copy the URL. */}
            <div className="grid grid-cols-4 gap-2 mb-3" data-testid="cb-social-row">
              {(() => {
                const u = encodeURIComponent(cookbookUrl);
                const t = encodeURIComponent(cb.title || "Cookbook");
                const tx = encodeURIComponent(`Check out this cookbook: ${cb.title}`);
                const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=640,height=580");
                const copyAndToast = async (msg) => {
                  await navigator.clipboard.writeText(cookbookUrl);
                  toast.success(msg, { duration: 4500 });
                };
                const tile = (key, Icon, label, onClick, hint) => (
                  <button
                    type="button"
                    data-testid={`cb-share-${key}`}
                    onClick={onClick}
                    className="flex flex-col items-center justify-center gap-1 p-2 rounded-xl bg-white hover:bg-rf-amber/20 border border-rf-ink/10 transition-all hover:-translate-y-0.5"
                  >
                    <Icon size={18} weight="fill" className="text-rf-ink" />
                    <span className="text-[10px] font-bold text-rf-ink">{label}</span>
                    {hint && <span className="text-[9px] text-rf-ink-soft">{hint}</span>}
                  </button>
                );
                return (<>
                  {tile("facebook", FacebookLogo, "Facebook", () => open(`https://www.facebook.com/sharer/sharer.php?u=${u}`))}
                  {tile("x", XLogo, "X", () => open(`https://twitter.com/intent/tweet?url=${u}&text=${tx}`))}
                  {tile("whatsapp", WhatsappLogo, "WhatsApp", () => open(`https://wa.me/?text=${tx}%20${u}`))}
                  {tile("telegram", TelegramLogo, "Telegram", () => open(`https://t.me/share/url?url=${u}&text=${tx}`))}
                  {tile("reddit", RedditLogo, "Reddit", () => open(`https://www.reddit.com/submit?url=${u}&title=${t}`))}
                  {tile("instagram", InstagramLogo, "Instagram", () => copyAndToast("Link copied — paste it into your Instagram caption or Story."), "copies URL")}
                  {tile("tiktok", TiktokLogo, "TikTok", () => copyAndToast("Link copied — paste it into your TikTok caption or bio."), "copies URL")}
                  {tile("email", Envelope, "Email", () => { setShowQR(false); setEmailOpen(true); })}
                </>);
              })()}
            </div>
            <button onClick={() => setShowQR(false)} className="btn-amber w-full">Close</button>
          </div>
        </div>
      )}

      <EmailShareDialog open={emailOpen} onOpenChange={setEmailOpen} title={cb.title} url={cookbookUrl} summary={`Check out this cookbook: ${cb.title}`} kind="cookbook" />
      <ShareCookbookDialog
        open={showInvite}
        onOpenChange={setShowInvite}
        cookbook={cb}
      />

      <PrintImageGuardModal
        open={printGuardOpen}
        recipes={recipesNeedingImages}
        onClose={() => setPrintGuardOpen(false)}
        onAllResolved={async () => {
          await reloadRecipes();
          setPrintGuardOpen(false);
          await downloadBookPdf();
        }}
      />

      <PrintPrepareModal
        open={printPrepareOpen}
        onClose={() => setPrintPrepareOpen(false)}
        cookbook={cb}
        recipes={recipes}
        recipesNeedingImages={recipesNeedingImages}
      />

      {/* MOVE TO TRASH confirmation — reversible soft-delete. The book
          lands in the Dashboard's Trash section where a single Restore
          tap brings it back. Hard-delete happens automatically after
          30 days (cron) or via the Trash row's "Delete forever" button. */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-[60] bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { if (!deleting) setDeleteOpen(false); }}
          data-testid="cb-delete-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cb-delete-title"
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                <Trash size={22} weight="duotone" className="text-amber-700" aria-hidden="true" />
              </div>
              <h3 id="cb-delete-title" className="font-display font-black text-2xl text-rf-ink leading-tight">
                Move to Trash?
              </h3>
            </div>
            <p className="text-sm text-rf-ink mb-2">
              <b>&ldquo;{cb.title}&rdquo;</b> and its <b>{recipes.length}</b> {recipes.length === 1 ? "recipe" : "recipes"} will be hidden from your dashboard and public links.
            </p>
            <p className="text-sm text-rf-ink-soft mb-5">
              You have <b>30 days</b> to restore it from your Dashboard&rsquo;s <b>Trash</b> section. After 30 days everything (recipes, photos, scans, audio) is permanently deleted and can&rsquo;t be recovered.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="cb-delete-cancel"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
                className="btn-outline-ink justify-center flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="cb-delete-confirm"
                onClick={handleDeleteCookbook}
                disabled={deleting}
                className="justify-center flex-1 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? "Moving…" : "Move to Trash"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Friendly rename prompt for freshly imported cookbooks */}
      {renamePrompt && (
        <div className="fixed inset-0 z-50 bg-rf-ink/70 flex items-center justify-center p-4" data-testid="cb-rename-prompt">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-rf-amber/20 flex items-center justify-center">
                <PencilSimple size={22} weight="duotone" className="text-rf-orange" />
              </div>
              <div>
                <h2 className="font-display font-black text-xl text-rf-ink">Give this cookbook a proper name?</h2>
                <p className="text-xs text-rf-ink-soft">You can always rename it later from the cookbook editor.</p>
              </div>
            </div>
            <p className="text-sm text-rf-ink-soft mb-3">
              Your import was saved as <span className="font-semibold text-rf-ink">"{cb?.title}"</span>. Rename it to
              something you'll recognise on your shelf — like "Nan's kitchen" or "Weeknight favourites".
            </p>
            <input
              autoFocus
              type="text"
              value={renamePrompt.title}
              onChange={(e) => setRenamePrompt({ title: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && renamePrompt.title.trim() && saveRename()}
              maxLength={120}
              data-testid="cb-rename-input"
              className="w-full px-4 py-3 rounded-lg border-2 border-rf-ink/10 focus:border-rf-orange bg-white text-rf-ink outline-none"
              placeholder="e.g. Nan's kitchen"
            />
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setRenamePrompt(null)}
                data-testid="cb-rename-keep"
                className="px-4 py-2 rounded-full text-sm font-semibold text-rf-ink-soft hover:text-rf-ink">
                Keep as "{cb?.title?.slice(0, 30)}{(cb?.title || "").length > 30 ? "…" : ""}"
              </button>
              <button
                onClick={saveRename}
                disabled={savingRename || !renamePrompt.title.trim() || renamePrompt.title.trim() === cb?.title}
                data-testid="cb-rename-save"
                className="px-5 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-50">
                {savingRename ? "Renaming…" : "Rename"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
